import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useOrgConfig } from '../contexts/OrgConfigContext';
import { OrgConfig, ItemType } from '../types';

const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

const FIELD_LABELS: Record<string, string> = {
  track_manufacturer: 'Manufacturer',
  track_model: 'Model',
  track_size: 'Size',
  track_serial_number: 'Serial Number',
  track_date_manufactured: 'Date Manufactured',
  track_condition: 'Condition',
  track_assigned_to: 'Assigned To',
  track_station: 'Station',
  track_notes: 'Notes',
  track_gear_classification: 'Classification',
};

const TRACK_FIELDS = Object.keys(FIELD_LABELS);

export default function DepartmentConfig() {
  const { user } = useAuth();
  const { refresh: refreshOrgConfig } = useOrgConfig();
  const isAdmin = user?.role === 'admin';
  const [config, setConfig] = useState<OrgConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [fiscalMonth, setFiscalMonth] = useState(7);

  // User permissions state
  const [perms, setPerms] = useState({
    user_can_edit_own_gear: true,
    user_can_change_classification: true,
    user_can_retire_own_gear: true,
    user_can_add_gear: true,
    user_can_archive_own_gear: true,
  });

  useEffect(() => {
    api.get<OrgConfig>('/org/config')
      .then(data => {
        setConfig(data);
        setFiscalMonth(data.organization.fiscal_year_start_month);
        setPerms({
          user_can_edit_own_gear: data.organization.user_can_edit_own_gear ?? true,
          user_can_change_classification: data.organization.user_can_change_classification ?? true,
          user_can_retire_own_gear: data.organization.user_can_retire_own_gear ?? true,
          user_can_add_gear: data.organization.user_can_add_gear ?? true,
          user_can_archive_own_gear: data.organization.user_can_archive_own_gear ?? true,
        });
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const showSuccess = (msg: string) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleSaveFiscalYear = async () => {
    setSaving('fiscal');
    setError('');
    try {
      await api.put('/org/config', { fiscal_year_start_month: fiscalMonth });
      showSuccess('Fiscal year updated');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(null);
    }
  };

  const handleSavePermissions = async () => {
    setSaving('perms');
    setError('');
    try {
      await api.put('/org/config', perms);
      await refreshOrgConfig();
      showSuccess('User permissions updated');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(null);
    }
  };

  const handleToggleField = async (typeId: string, field: string, value: boolean) => {
    setSaving(`${typeId}-${field}`);
    setError('');
    try {
      await api.put(`/item-types/${typeId}`, { [field]: value });
      const data = await api.get<OrgConfig>('/org/config');
      setConfig(data);
      await refreshOrgConfig();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(null);
    }
  };

  const handleUpdateItemType = async (typeId: string, updates: Record<string, unknown>) => {
    setSaving(typeId);
    setError('');
    try {
      await api.put(`/item-types/${typeId}`, updates);
      const data = await api.get<OrgConfig>('/org/config');
      setConfig(data);
      showSuccess('Item type updated');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>Loading...</div>;
  if (!config) return <div style={{ padding: 40, textAlign: 'center', color: '#dc2626' }}>Failed to load configuration</div>;

  const inputStyle = { padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, background: 'white' };

  return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 20px', color: '#111' }}>Dept Config</h1>

      {error && <div style={{ background: '#fef2f2', color: '#b91c1c', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 14 }}>{error}</div>}
      {success && <div style={{ background: '#f0fdf4', color: '#166534', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 14 }}>{success}</div>}

      {/* Organization Settings — Admin only */}
      {isAdmin && (
        <div style={{ background: 'white', borderRadius: 12, padding: 20, marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          <h2 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600 }}>Organization Settings</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <label style={{ fontSize: 14, fontWeight: 500 }}>Fiscal Year Starts:</label>
            <select value={fiscalMonth} onChange={e => setFiscalMonth(parseInt(e.target.value))}
              style={{ ...inputStyle, minWidth: 140 }}>
              {monthNames.map((name, i) => (
                <option key={i + 1} value={i + 1}>{name}</option>
              ))}
            </select>
            <button onClick={handleSaveFiscalYear} disabled={saving === 'fiscal'}
              style={{
                padding: '6px 16px', background: '#b91c1c', color: 'white', border: 'none',
                borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                opacity: saving === 'fiscal' ? 0.7 : 1,
              }}>
              {saving === 'fiscal' ? 'Saving...' : 'Save'}
            </button>
          </div>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 8 }}>
            Used for budget forecasting. Common: July (municipal), January (calendar), October (federal).
          </div>
        </div>
      )}

      {/* User Permissions — Admin only */}
      {isAdmin && (
        <div style={{ background: 'white', borderRadius: 12, padding: 20, marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          <h2 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 600 }}>Standard User Permissions</h2>
          <p style={{ margin: '0 0 16px', fontSize: 13, color: '#6b7280' }}>
            Control what standard (non-admin, non-quartermaster) users can do with their own gear.
          </p>
          <div style={{ display: 'grid', gap: 12 }}>
            {[
              { key: 'user_can_add_gear', label: 'Add new gear items', desc: 'Users can add PPE items assigned to themselves' },
              { key: 'user_can_edit_own_gear', label: 'Edit their own gear', desc: 'Users can modify details of gear assigned to them' },
              { key: 'user_can_change_classification', label: 'Change classification', desc: 'Users can set frontline/backup status on their gear' },
              { key: 'user_can_retire_own_gear', label: 'Retire their own gear', desc: 'Users can retire gear assigned to them' },
              { key: 'user_can_archive_own_gear', label: 'Delete (archive) gear', desc: 'Users can soft-delete gear assigned to them' },
            ].map(p => (
              <label key={p.key}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 14px',
                  background: (perms as Record<string, boolean>)[p.key] ? '#f0fdf4' : '#f9fafb',
                  borderRadius: 8, cursor: 'pointer', border: '1px solid',
                  borderColor: (perms as Record<string, boolean>)[p.key] ? '#bbf7d0' : '#e5e7eb',
                  transition: 'all 0.15s ease',
                }}>
                <input type="checkbox"
                  checked={(perms as Record<string, boolean>)[p.key]}
                  onChange={e => setPerms(prev => ({ ...prev, [p.key]: e.target.checked }))}
                  style={{ marginTop: 2, accentColor: '#16a34a' }} />
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500, color: '#111' }}>{p.label}</div>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>{p.desc}</div>
                </div>
              </label>
            ))}
          </div>
          <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end' }}>
            <button onClick={handleSavePermissions} disabled={saving === 'perms'}
              style={{
                padding: '8px 20px', background: '#b91c1c', color: 'white', border: 'none',
                borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 600,
                opacity: saving === 'perms' ? 0.7 : 1,
              }}>
              {saving === 'perms' ? 'Saving...' : 'Save Permissions'}
            </button>
          </div>
        </div>
      )}

      {/* Item Types — Budget & Fields */}
      {config.item_types.map(type => (
        <ItemTypeCard
          key={type.id}
          type={type}
          saving={saving}
          onToggleField={handleToggleField}
          onSaveBudget={handleUpdateItemType}
        />
      ))}
    </div>
  );
}

function ItemTypeCard({ type, saving, onToggleField, onSaveBudget }: {
  type: ItemType;
  saving: string | null;
  onToggleField: (typeId: string, field: string, value: boolean) => Promise<void>;
  onSaveBudget: (typeId: string, updates: Record<string, unknown>) => Promise<void>;
}) {
  const [cost, setCost] = useState(type.estimated_cost?.toString() || '');
  const [life, setLife] = useState(type.service_life_years?.toString() || '10');
  const [budgetDirty, setBudgetDirty] = useState(false);

  const inputStyle = { padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, background: 'white', width: 90, textAlign: 'right' as const };

  return (
    <div style={{ background: 'white', borderRadius: 12, padding: 20, marginBottom: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#111' }}>
            {type.name}
            {type.is_default && <span style={{ marginLeft: 8, fontSize: 11, color: '#6b7280', fontWeight: 400 }}>Default</span>}
          </h3>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ color: '#6b7280' }}>Cost $</span>
            <input type="number" value={cost} onChange={e => { setCost(e.target.value); setBudgetDirty(true); }}
              placeholder="--" step="0.01" min="0" style={inputStyle} />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ color: '#6b7280' }}>Life</span>
            <input type="number" value={life} onChange={e => { setLife(e.target.value); setBudgetDirty(true); }}
              min="1" max="50" style={{ ...inputStyle, width: 50 }} />
            <span style={{ color: '#6b7280' }}>yrs</span>
          </label>
          {budgetDirty && (
            <button onClick={() => {
              onSaveBudget(type.id, {
                estimated_cost: cost ? parseFloat(cost) : null,
                service_life_years: parseInt(life) || 10,
              });
              setBudgetDirty(false);
            }}
              disabled={saving === type.id}
              style={{
                padding: '4px 12px', background: '#b91c1c', color: 'white', border: 'none',
                borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                opacity: saving === type.id ? 0.7 : 1,
              }}>
              {saving === type.id ? '...' : 'Save'}
            </button>
          )}
        </div>
      </div>

      {/* Field toggles */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {TRACK_FIELDS.map(field => {
          const enabled = (type as unknown as Record<string, unknown>)[field] as boolean;
          const isSaving = saving === `${type.id}-${field}`;
          return (
            <button
              key={field}
              onClick={() => onToggleField(type.id, field, !enabled)}
              disabled={isSaving}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '5px 10px', borderRadius: 6, fontSize: 12, fontWeight: 500,
                border: '1px solid',
                borderColor: enabled ? '#86efac' : '#d1d5db',
                background: enabled ? '#f0fdf4' : '#fff',
                color: enabled ? '#166534' : '#6b7280',
                cursor: 'pointer',
                opacity: isSaving ? 0.6 : 1,
                transition: 'all 0.15s ease',
              }}>
              <span style={{ fontSize: 11 }}>{enabled ? '✓' : ''}</span>
              {FIELD_LABELS[field]}
            </button>
          );
        })}
      </div>
    </div>
  );
}
